package fr.arcplex.network.impl.legacy

import fr.arcplex.network.AuthProvider
import fr.arcplex.network.http.JsonMap
import fr.arcplex.network.logging.Logger
import fr.arcplex.network.model.Rail
import fr.arcplex.network.model.Screen
import fr.arcplex.network.model.ScreenKind
import fr.arcplex.network.model.isSearchRail
import fr.arcplex.network.model.parseRailItems
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Maps a legacy page JSON to the unified [Screen], loading each rail's items
 * (menu, fixed element, lines) in parallel like the UI used to.
 */
internal class LegacyScreenMapper(
    private val client: DefaultApi,
    private val auth: AuthProvider
) {
    suspend fun parse(id: String?, json: JsonMap, kind: ScreenKind = ScreenKind.NORMAL): Screen =
        coroutineScope {
            val params = json.get("parameters")

            val menuDeferred = async { buildRail(json.get("menu")) }
            val heroDeferred = async { buildRail(json.get("fixed_element")) }
            val railsDeferred = (json.get("lines")?.getList() ?: emptyList())
                .map { line -> async { line?.let { buildRail(it) } } }

            val rails = railsDeferred.awaitAll().filterNotNull()
            val resolvedKind = when {
                kind == ScreenKind.FILM -> ScreenKind.FILM
                rails.any { it.isSearchRail } -> ScreenKind.SEARCH
                else -> kind
            }

            Screen(
                id = id,
                kind = resolvedKind,
                label = params?.getString("label") ?: "",
                backgroundImage = params?.getString("bg_image"),
                backgroundHex = params?.getString("bg_color") ?: "#0A0E17",
                menu = menuDeferred.await(),
                hero = heroDeferred.await(),
                rails = rails
            )
        }

    @OptIn(ExperimentalUuidApi::class)
    private suspend fun buildRail(mapWrapper: JsonMap?): Rail {
        val map = mapWrapper?.getList()?.get(0) ?: mapWrapper ?: return emptyRail()
        val lineHeight = map.getNumber("line_height")?.toFloat()
        val sizeLabels = map.get("params_labels")?.getNumber("size_labels")?.toFloat()
        val displaySize = if (map.getString("bl_display_object_label") == "true") {
            (map.get("params_labels")?.getNumber("size_label_object")?.toFloat() ?: 60f) + 10
        } else {
            0f
        }
        val displayLabel = map.getString("bl_display_label") == "true"
        return loadRail(
            id = map.getString("id") ?: "",
            idLigne = map.getString("id_ligne") ?: "",
            idUrl = map.getString("id_url") ?: "",
            url = map.getString("url") ?: "",
            label = if (displayLabel) map.getString("label") else null,
            type = map.getString("type") ?: "txt",
            canFocus = (map.getNumber("can_focus") ?: 0.0) == 1.0,
            height = (lineHeight ?: 60f) + (sizeLabels ?: 0f) + 10f + displaySize,
            uid = map.getString("uid") ?: Uuid.generateV7().toHexString()
        )
    }

    @OptIn(ExperimentalUuidApi::class)
    private suspend fun loadRail(
        id: String,
        idLigne: String,
        idUrl: String,
        url: String,
        label: String?,
        type: String,
        canFocus: Boolean?,
        height: Float,
        uid: String
    ): Rail {
        val token = auth.getUserToken()
        val lineContent = client.getGenericCallURL(idUrl, url, token)
        var objects = lineContent?.get("content")?.get("objects")
        if (objects == null) {
            val contentUrl = lineContentUrl(url)
            if (contentUrl != null) {
                Logger.d("LINE", "no objects, retrying as line content: $contentUrl")
                objects = client.getGenericCallURL(idUrl, contentUrl, token)?.get("content")?.get("objects")
            }
        }
        return Rail(
            id = idLigne.ifEmpty { id },
            uid = uid.ifEmpty { Uuid.generateV7().toHexString() },
            label = label,
            type = type,
            height = height,
            focusable = canFocus ?: true,
            items = parseRailItems(objects)
        )
    }

    private fun emptyRail() = Rail(id = "", uid = "", type = "", height = 60f, focusable = true)
}
